/* Phase 5 · Visitor Management (guard station)
 * Log walk-in visitors with photos of person + vehicle, phone-friendly
 * camera capture, one-tap check-out on exit, and an end-of-day printable log.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Camera, Car, LogOut, Printer, User as UserIcon } from "lucide-react";

const EMPTY = {
  name: "", purpose: "", host_name: "", phone: "",
  id_type: "Aadhaar", id_number: "", vehicle_no: "",
  visitor_photo_file_id: null, vehicle_photo_file_id: null,
};

async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  // GPS: try to grab, fallback to 0/0 (server accepts image uploads with GPS
  // present; guard station is a fixed post, so both work).
  const gps = await new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: "0", lng: "0" });
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: String(p.coords.latitude), lng: String(p.coords.longitude) }),
      () => resolve({ lat: "0", lng: "0" }),
      { timeout: 4000 },
    );
  });
  fd.append("gps_lat", gps.lat);
  fd.append("gps_lng", gps.lng);
  fd.append("taken_at", new Date().toISOString());
  const r = await fetch(`${API}/files/upload`, {
    method: "POST", body: fd, credentials: "include",
  });
  if (!r.ok) throw new Error(await r.text());
  const j = await r.json();
  return j.file_id || j.id;   // legacy compat
}

export default function VisitorLog() {
  const { lang } = useLang();
  const [visitors, setVisitors] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [uploading, setUploading] = useState({ visitor: false, vehicle: false });
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const r = await api.get("/visitors", { params: { date: today } });
    setVisitors(r.data);
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const pickPhoto = async (kind, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [kind]: true }));
    try {
      const fid = await uploadPhoto(file);
      setForm(f => ({ ...f, [kind === "visitor" ? "visitor_photo_file_id" : "vehicle_photo_file_id"]: fid }));
      toast.success(`${kind} photo saved`);
    } catch (e) {
      toast.error(`Photo upload failed`);
    } finally {
      setUploading(u => ({ ...u, [kind]: false }));
    }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error(lang === "hi" ? "नाम आवश्यक" : "Name required");
    setSaving(true);
    try {
      const r = await api.post("/visitors", form);
      toast.success(`${r.data.visitor_no} · ${lang === "hi" ? "अंदर" : "checked in"}`);
      setForm({ ...EMPTY });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setSaving(false);
  };

  const checkOut = async (v) => {
    if (!window.confirm(lang === "hi" ? `${v.name} को बाहर करें?` : `Check out ${v.name}?`)) return;
    try {
      await api.post(`/visitors/${v.id}/check-out`, {});
      toast.success(`${v.name} · ${lang === "hi" ? "बाहर" : "out"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const stillIn = visitors.filter(v => !v.out_time);
  const done = visitors.filter(v => v.out_time);

  return (
    <div className="min-h-screen bg-[#F2ECE0]" data-testid="visitor-log">
      <div className="max-w-3xl mx-auto p-4">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">
              {lang === "hi" ? "गेट · विज़िटर" : "GATE · VISITORS"}
            </div>
            <h1 className="text-2xl font-bold text-[#0B2340]">
              {lang === "hi" ? "विज़िटर मैनेजमेंट" : "Visitor Management"}
            </h1>
          </div>
          <Link to={`/gate/visitors/eod?date=${today}`} target="_blank" rel="noopener"
            data-testid="visitor-eod-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0B2340] text-white text-xs">
            <Printer size={12} /> {lang === "hi" ? "आज का रजिस्टर छापें" : "End-of-day print"}
          </Link>
        </div>

        {/* Log a new visitor */}
        <div className="bg-white p-4 rounded-lg border border-slate-100 mb-6">
          <h3 className="text-sm font-semibold text-[#0B2340] mb-3">
            {lang === "hi" ? "नया विज़िटर" : "Log a visitor"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <input placeholder={lang === "hi" ? "नाम *" : "Name *"} data-testid="v-name"
              className="border rounded px-2 py-1.5" value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} />
            <input placeholder={lang === "hi" ? "फ़ोन" : "Phone"}
              className="border rounded px-2 py-1.5" value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} />
            <input placeholder={lang === "hi" ? "मिलना है" : "Purpose"} data-testid="v-purpose"
              className="border rounded px-2 py-1.5" value={form.purpose}
              onChange={e => setForm({ ...form, purpose: e.target.value })} />
            <input placeholder={lang === "hi" ? "होस्ट" : "Host"}
              className="border rounded px-2 py-1.5" value={form.host_name}
              onChange={e => setForm({ ...form, host_name: e.target.value })} />
            <select className="border rounded px-2 py-1.5" value={form.id_type}
              onChange={e => setForm({ ...form, id_type: e.target.value })}>
              {["Aadhaar", "DL", "PAN", "Voter ID", "Passport", "Other"].map(x => <option key={x}>{x}</option>)}
            </select>
            <input placeholder={lang === "hi" ? "पहचान न." : "ID number"}
              className="border rounded px-2 py-1.5" value={form.id_number}
              onChange={e => setForm({ ...form, id_number: e.target.value })} />
            <input placeholder={lang === "hi" ? "वाहन न." : "Vehicle no."} data-testid="v-vehicle"
              className="border rounded px-2 py-1.5 font-mono uppercase md:col-span-2" value={form.vehicle_no}
              onChange={e => setForm({ ...form, vehicle_no: e.target.value.toUpperCase() })} />
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            <PhotoField
              icon={<UserIcon size={16} />}
              label={lang === "hi" ? "विज़िटर फ़ोटो" : "Visitor photo"}
              testid="v-photo-visitor"
              file_id={form.visitor_photo_file_id}
              uploading={uploading.visitor}
              onPick={file => pickPhoto("visitor", file)}
            />
            <PhotoField
              icon={<Car size={16} />}
              label={lang === "hi" ? "वाहन फ़ोटो" : "Vehicle photo"}
              testid="v-photo-vehicle"
              file_id={form.vehicle_photo_file_id}
              uploading={uploading.vehicle}
              onPick={file => pickPhoto("vehicle", file)}
            />
          </div>

          <button onClick={submit} disabled={saving} data-testid="v-checkin"
            className="mt-3 w-full py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">
            {saving ? "…" : (lang === "hi" ? "✓ चेक-इन" : "✓ Check in")}
          </button>
        </div>

        {/* Still on premises */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-[#0B2340] mb-2">
            {lang === "hi" ? "अभी अंदर" : "Still on premises"} ({stillIn.length})
          </h3>
          {stillIn.length === 0 ? (
            <div className="text-xs text-[#7C8E9E] italic">
              {lang === "hi" ? "कोई विज़िटर अंदर नहीं।" : "No visitors currently in."}
            </div>
          ) : (
            <div className="space-y-2">
              {stillIn.map(v => (
                <VisitorCard key={v.id} v={v} onCheckOut={() => checkOut(v)} lang={lang} />
              ))}
            </div>
          )}
        </div>

        {/* Already left */}
        {done.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-[#7C8E9E] mb-2 uppercase tracking-widest text-[11px]">
              {lang === "hi" ? "आज बाहर हुए" : `Checked out today (${done.length})`}
            </h3>
            <div className="space-y-1">
              {done.map(v => <VisitorCard key={v.id} v={v} lang={lang} compact />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PhotoField({ icon, label, testid, file_id, uploading, onPick }) {
  return (
    <label className={`relative flex flex-col items-center justify-center p-3 rounded-lg border-2 border-dashed
      ${file_id ? "border-emerald-500 bg-emerald-50" : "border-[#4A627A]/30 bg-slate-50"}
      cursor-pointer text-xs text-center min-h-[80px]`}>
      {uploading ? (
        <span className="text-[#7C8E9E]">Uploading…</span>
      ) : file_id ? (
        <>
          <div className="text-emerald-700 flex items-center gap-1"><Camera size={14} /> Saved</div>
          <div className="text-[10px] text-[#7C8E9E] mt-0.5">tap to retake</div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-1 text-[#4A627A]">{icon} {label}</div>
          <div className="text-[10px] text-[#7C8E9E] mt-0.5">tap to capture</div>
        </>
      )}
      <input type="file" accept="image/*" capture="environment"
        data-testid={testid}
        className="hidden"
        onChange={e => onPick(e.target.files?.[0])} />
    </label>
  );
}

function VisitorCard({ v, onCheckOut, lang, compact = false }) {
  return (
    <div className={`bg-white border border-slate-100 rounded-lg p-3 ${compact ? "" : "shadow-sm"}`}
      data-testid={`visitor-${v.visitor_no}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-[11px] text-[#7C8E9E]">{v.visitor_no}</span>
            <span className="font-semibold text-[#0B2340]">{v.name}</span>
            {v.vehicle_no && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 uppercase">
                {v.vehicle_no}
              </span>
            )}
          </div>
          <div className="text-xs text-[#4A627A] mt-0.5">
            {v.purpose || "—"} · {v.host_name ? `→ ${v.host_name}` : ""}
          </div>
          <div className="text-[10px] text-[#7C8E9E] mt-0.5">
            In {(v.in_time || "").slice(11, 16)}
            {v.out_time ? ` · Out ${v.out_time.slice(11, 16)}` : ""}
            {v.phone ? ` · ${v.phone}` : ""}
          </div>
        </div>
        {onCheckOut && (
          <button onClick={onCheckOut} data-testid={`visitor-out-${v.visitor_no}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-semibold">
            <LogOut size={12} /> {lang === "hi" ? "बाहर" : "Out"}
          </button>
        )}
      </div>
    </div>
  );
}
